import { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { useNotify } from "@/contexts/NotifyContext";
import { useAuth } from "@/contexts/AuthContext";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { register } from "@/services/DirectoryService";
import { type UserIASMA } from "iasma-datatypes";
import axios from "axios";

let submitStatus: boolean = false;

export function RegisterForm() {

	const { session, signout, setToken } = useAuth();
	const { setNotify } = useNotify();
	const [errors, setErrors] = useState<any>({});
	const [isSubmitted, setIsSubmitted] = useState(submitStatus);
	const { executeRecaptcha } = useGoogleReCaptcha();

	const resetForm = {
		email: session?.User?.Email || '',
		nama: '',
		angkatan: '',
		kelas: '',
		keterangan: '',
		recaptcha: ''
	}
	const [formData, setFormData] = useState(resetForm);

	useEffect(() => {
		if (session) {
			setFormData(prev => ({
				...prev,
				nama: session.User?.Name || prev.nama
			}));
		}
	}, [session]);

	useEffect(() => {
		if (submitStatus != isSubmitted) {
			submitStatus = isSubmitted;
		}
	}, [isSubmitted]);

	// Generate years for dropdown (current year - 50 to current year + 10)
	const currentYear = new Date().getFullYear();
	const years = [];
	for (let i = 1959; i <= currentYear - 1; i++) {
		years.push(i);
	}

	const handleChange = (e: any) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
		if (errors[name]) {
			setErrors((prev: any) => ({
				...prev,
				[name]: ''
			}));
		}
	};

	const validateForm = () => {
		const newErrors: any = {};

		if (!formData.email) {
			newErrors.email = 'Email wajib isi';
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			newErrors.email = 'Email salah';
		}

		if (!formData.nama) {
			newErrors.nama = 'Nama wajib isi';
		} else if (formData.nama.length < 2) {
			newErrors.nama = 'Nama harus minimal 2 karakter';
		}

		if (!formData.angkatan) {
			newErrors.angkatan = 'Tahun kelulusan wajib isi';
		}

		if (!formData.kelas) {
			newErrors.kelas = 'Kelas wajib isi';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async () => {
		if (validateForm()) {
			setNotify(null);
			try {
				if (!executeRecaptcha) {
					console.log('Execute recaptcha not yet available');
					return;
				}

				const token = await executeRecaptcha('register');
				let data = { ...formData, recaptcha: token, avatar: session?.User?.Avatar || '' };
				try {
					setToken(axios);
					let result: any = await register(data);
					// console.log('register result:', result);
					if (result.status) {
						console.log('Registration successful!');
						setIsSubmitted(true);
						submitStatus = true;
						localStorage.setItem('registerSubmitted', 'true');
						// setFormData(resetForm);
					} else {
						setNotify({ title: "Registration Failed", error: { message: result.message }, severity: 'error' });
					}
				} catch (error: any) {
					setNotify({ title: "Registration Failed", error, severity: 'error' });
				};

			} catch (error: any) {
				setNotify({ title: "Registration Failed", error, severity: 'error' });
			}
		}
	};

	// const handleReset = () => {
	// 	setFormData(resetForm);
	// 	setErrors({});
	// 	setIsSubmitted(false);
	// };

	const handleSignOut = () => {
		submitStatus = false
		setIsSubmitted(false)
		signout()
	}

	// console.log("isSubmitted", isSubmitted);

	return (
		<div className="transition-colors duration-300 flex items-center justify-center p-6 bg-(--color-background)">
			<div className="w-full max-w-md">
				{/* Success Message */}
				{isSubmitted && (
					<div>
						<div className="mb-6 px-4 py-3 rounded-lg border-2 bg-green-700 border-green-800 text-green-50">
							<div className="flex items-center">
								<svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
								</svg>
								<span className="font-medium">Registrasi berhasil!</span>
							</div>
						</div>
						<p>Terima kasih telah melakukan registrasi. Konfirmasi akan dikirimkan ke email Anda oleh admin.</p>
						<br />
						<button
							onClick={handleSignOut}
							className="px-6 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors border-2 bg-(--color-surface) hover:bg-(--color-surface-hover) border-(--color-border) text-(--color-text)"
						>
							Sign Out
						</button>
					</div>
				)}

				{/* Registration Form */}
				{!isSubmitted && (
					<div className="transition-colors">
						<div className="text-center mb-8">
							<h1 className="text-3xl font-bold mb-2 text-(--color-primary)">
								Registrasi Alumni
							</h1>
							<p className="text-xs text-(--color-text-secondary) text-justify">Email Anda belum terdaftar dalam basis data Alumni.
								Jika Anda merupakan <b>alumni</b> dari <b>SMA 1 Bukittinggi</b>, silakan melakukan registrasi dengan mengisi data diri pada formulir di bawah ini.
								Data yang Anda berikan akan digunakan sebagai bahan validasi oleh admin angkatan terkait.
							</p>
						</div>

						<div className="space-y-6">
							{/* Name Field */}
							<div>
								<label htmlFor="nama" className="block text-sm font-medium mb-2 text-(--color-text)">
									Nama Lengkap *
								</label>
								<input
									type="text"
									id="nama"
									name="nama"
									value={formData.nama || ''}
									onChange={handleChange}
									className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors bg-(--color-background) border-2 text-(--color-text) ${errors.nama ? 'border-red-500' : 'border-(--color-border)'}`}
									placeholder="Isi nama lengkap Anda"
								/>
								{errors.nama && (
									<p className="mt-1 text-sm text-red-500">{errors.nama}</p>
								)}
							</div>

							{/* Email Field */}
							<div>
								<label htmlFor="email" className="block text-sm font-medium mb-2 text-(--color-text)">
									Alamat Email *
								</label>
								<input
									type="email"
									id="email"
									name="email"
									value={session?.User?.Email || formData.email}
									onChange={handleChange}
									className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors bg-(--color-background) border-2 text-(--color-text) ${errors.email ? 'border-red-500' : 'border-(--color-border)'}`}
									readOnly={!!session?.User?.Email}
									placeholder={session?.User?.Email || "your.email@example.com"}
								/>
								{errors.email && (
									<p className="mt-1 text-sm text-red-500">{errors.email}</p>
								)}
							</div>

							{/* Year of Graduate Field */}
							<div>
								<label htmlFor="angkatan" className="block text-sm font-medium mb-2 text-(--color-text)">
									Angkatan (Tahun Lulus) *
								</label>
								<select
									id="angkatan"
									name="angkatan"
									value={formData.angkatan}
									onChange={handleChange}
									className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors bg-(--color-background) border-2 text-(--color-text) ${errors.angkatan ? 'border-red-500' : 'border-(--color-border)'}`}
								>
									<option value="">Pilih angkatan</option>
									{years.reverse().map(year => (
										<option key={year} value={year}>{year}</option>
									))}
								</select>
								{errors.angkatan && (
									<p className="mt-1 text-sm text-red-500">{errors.angkatan}</p>
								)}
							</div>

							{/* Class in School Field */}
							<div>
								<label htmlFor="kelas" className="block text-sm font-medium mb-2 text-(--color-text)">
									Kelas selama di sekolah *
								</label>
								<input
									type="text"
									id="kelas"
									name="kelas"
									value={formData.kelas}
									onChange={handleChange}
									className={`w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors bg-(--color-background) border-2 text-(--color-text) ${errors.kelas ? 'border-red-500' : 'border-(--color-border)'}`}
									placeholder="misal: Kelas 1-1, Kelas 2-IPA-2, Kelas 3-IPS-3 dst."
								/>
								{errors.kelas && (
									<p className="mt-1 text-sm text-red-500">{errors.kelas}</p>
								)}
							</div>

							{/* Keterangan Field */}
							<div>
								<label htmlFor="keterangan" className="block text-sm font-medium mb-2 text-(--color-text)">
									Keterangan
								</label>
								<input
									type="text"
									id="keterangan"
									name="keterangan"
									value={formData.keterangan}
									onChange={handleChange}
									className='w-full px-4 py-3 rounded-lg focus:outline-none focus:ring-2 transition-colors bg-(--color-background) border-2 text-(--color-text) border-(--color-border)'
									placeholder="informasi tambahan jika ada"
								/>
							</div>

							{/* Buttons */}
							<div className="flex gap-4 pt-4">
								<button
									onClick={handleSubmit}
									className="flex-1 py-3 px-6 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors text-white bg-(--color-primary) hover:bg-(--color-primary-hover)"
								>
									Register
								</button>
								<button
									onClick={signout}
									className="px-6 py-3 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors border-2 bg-(--color-surface) hover:bg-(--color-surface-hover) border-(--color-border) text-(--color-text)"
								>
									Sign Out
								</button>
							</div>
							<span className="text-xs">This form is protected by <a href="https://developers.google.com/recaptcha/docs/v3">reCAPTCHA v3</a></span>
						</div>
					</div>
				)}
			</div>
		</div>
	);
}